import React from "react";
import { Link } from "react-router-dom";
import { Phone, Clock, ArrowRight, CircleCheck, MapPin } from "lucide-react";
import { SITE } from "../lib/site";
import { useLocation as useGeo } from "../contexts/LocationContext";
import SectionHeading from "../components/SectionHeading";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function Zone() {
  const { displayCity, city } = useGeo();
  useDocumentTitle(
    city
      ? `Électricien à ${city} et alentours — Zone d'intervention | Voltis`
      : "Zone d'intervention — Voltis Électricité",
    city
      ? `Électricien à ${city} : nous intervenons dans votre ville et toutes les communes voisines. Voir la carte.`
      : "Électricien : nous intervenons dans votre ville et les communes voisines."
  );
  return (
    <div data-testid="zone-page">
      {/* HERO */}
      <section className="bg-slate-50">
        <div className="container-x py-16 lg:py-24">
          <div className="max-w-3xl">
            <span className="accent-bar mb-5" />
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-slate-900 mb-3">
              Notre secteur d'intervention
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              {city ? (
                <>
                  Intervention rapide à{" "}
                  <span className="text-[#EAB308]">{city}</span> et alentours.
                </>
              ) : (
                <>
                  Intervention rapide{" "}
                  <span className="text-[#EAB308]">près de chez vous.</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              {city ? (
                <>
                  Que vous soyez à <strong>{city}</strong> ou dans une commune voisine,
                  nous nous déplaçons rapidement.
                </>
              ) : (
                <>
                  Nous nous déplaçons rapidement <strong>près de chez vous</strong> et dans
                  toutes les communes voisines.
                </>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* MAP CENTRÉE SUR LA VILLE DÉTECTÉE */}
      <section className="section-padding bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Localisation"
              title={city ? `${city}, c'est ici.` : "Votre ville, c'est ici."}
              description={
                city
                  ? `Nous intervenons à ${city} et dans toutes les communes situées dans un rayon élargi. Délai moyen : 30 minutes en zone urbaine.`
                  : "Nous intervenons dans votre ville et dans toutes les communes voisines. Délai moyen : 30 minutes en zone urbaine."
              }
            />

            <ul className="mt-10 space-y-4">
              {[
                { icon: MapPin, text: city ? `Centré sur ${city}` : "Centré sur votre position" },
                { icon: Clock, text: "Intervention en 30 min en moyenne" },
                { icon: CircleCheck, text: "Disponible 7j/7, urgences 24/7" },
              ].map((it) => (
                <li key={it.text} className="flex items-start gap-3 text-slate-700">
                  <span className="inline-flex w-9 h-9 shrink-0 items-center justify-center bg-[#EAB308] text-slate-900">
                    <it.icon className="w-4 h-4" strokeWidth={2.5} />
                  </span>
                  <span className="pt-1.5">{it.text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 bg-slate-50 border-l-4 border-[#EAB308]">
              <p className="text-sm text-slate-600 leading-relaxed">
                <strong className="text-slate-900">Vous n'êtes pas certain d'être dans notre zone ?</strong>{" "}
                Contactez-nous, nous étudions chaque demande et nous déplaçons souvent au-delà
                de notre secteur habituel pour les chantiers importants.
              </p>
            </div>

            <div className="mt-8">
              <Link to="/contact" data-testid="zone-quote-btn" className="btn-primary">
                Demander un devis <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-7">
            <div className="aspect-[4/3] lg:aspect-[16/12] border border-slate-200 overflow-hidden">
              <iframe
                title="Zone d'intervention Voltis Électricité"
                data-testid="zone-map-iframe"
                src={`https://www.google.com/maps?q=${encodeURIComponent(city || 'France')}&z=11&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* AVAILABILITY */}
      <section className="section-padding bg-[#0F172A] text-white">
        <div className="container-x grid lg:grid-cols-3 gap-8">
          {[
            { icon: Clock, title: "Disponibilité", desc: SITE.hours },
            { icon: Phone, title: "Téléphone", desc: SITE.phoneDisplay, link: `tel:${SITE.phoneTel}` },
            { icon: CircleCheck, title: "Délai d'arrivée", desc: "30 minutes en moyenne en zone urbaine" },
          ].map((b) => (
            <div key={b.title} className="border border-white/10 p-8">
              <b.icon className="w-8 h-8 text-[#EAB308] mb-5" strokeWidth={1.5} />
              <h4 className="font-heading text-xl font-bold mb-2">{b.title}</h4>
              {b.link ? (
                <a href={b.link} className="text-slate-300 hover:text-[#EAB308] transition-colors">{b.desc}</a>
              ) : (
                <p className="text-slate-300">{b.desc}</p>
              )}
            </div>
          ))}
        </div>
        <div className="container-x pb-8 -mt-2">
          <div className="text-center pt-8">
            <Link to="/contact" data-testid="zone-cta-btn" className="btn-primary">
              Vérifier ma zone — Demander un devis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
